import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Activity, CalendarClock, CheckCircle2, ChevronRight, Clock3, HeartPulse, LineChart, Plus, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { usePatients } from "@/lib/usePatients";
import { getRuntimeKnowledgeBase } from "@/lib/pharmacology/runtimeKnowledge";
import { buildBodyLoad } from "@/domain/pkBodyLoadEngine";
import { QuickIngestionWizard } from "@/components/clinical/QuickIngestionWizard";
import { VitalSignsPanel } from "@/components/clinical/VitalSignsPanel";
import { SessionCheckin } from "@/components/clinical/SessionCheckin";
import { PsyLogTimelinePanel } from "@/components/clinical/PsyLogTimelinePanel";
import { readPatientVitals } from "@/lib/clinical/vitalPersistence";
import { PRIMARY_PATIENT_ID, CLINICAL_WINDOW_START, CLINICAL_WINDOW_END } from "@/lib/clinical/singlePatientMode";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PATIENT_STORAGE_KEY = "clinicalos-my-space-patient";

function dayKey(value: string | Date) {
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function sameDayLabel(key: string) {
  const [year, month, day] = key.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(date) === dayKey(today)) return "Hoje";
  if (dayKey(date) === dayKey(yesterday)) return "Ontem";
  return new Intl.DateTimeFormat("pt-BR", { day: "numeric", month: "long" }).format(date);
}

function weekday(key: string) {
  const [year, month, day] = key.split("-").map(Number);
  return new Intl.DateTimeFormat("pt-BR", { weekday: "long" }).format(new Date(year, month - 1, day));
}

function timeLabel(value: string | Date) {
  return new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

function uniqueNames(doses: any[]) {
  return Array.from(new Set(doses.map((dose) => String(dose.substances?.name ?? dose.substance_name ?? "Substância").trim()).filter(Boolean)));
}

type DayGroup = {
  key: string;
  doses: any[];
  names: string[];
  first: Date;
  last: Date;
};

function groupDays(doses: any[]): DayGroup[] {
  const map = new Map<string, any[]>();
  for (const dose of doses) {
    if (!dose.actual_time) continue;
    const key = dayKey(dose.actual_time);
    map.set(key, [...(map.get(key) ?? []), dose]);
  }
  return Array.from(map.entries())
    .map(([key, rows]) => {
      const sorted = [...rows].sort((a, b) => new Date(a.actual_time).getTime() - new Date(b.actual_time).getTime());
      return {
        key,
        doses: sorted,
        names: uniqueNames(sorted),
        first: new Date(sorted[0].actual_time),
        last: new Date(sorted[sorted.length - 1].actual_time),
      };
    })
    .sort((a, b) => b.key.localeCompare(a.key));
}

function MiniDoseTrace({ group }: { group: DayGroup }) {
  const width = 180;
  const height = 64;
  const points = group.doses.slice(-8).map((dose, index, array) => {
    const date = new Date(dose.actual_time);
    const minute = date.getHours() * 60 + date.getMinutes();
    const x = 8 + (minute / 1440) * (width - 16);
    const y = height - 10 - ((index % 3) * 9 + Math.min(24, array.length * 2));
    return { x, y };
  });
  if (!points.length) return <div className="h-14 w-40" />;
  const d = points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-14 w-40 max-w-full" role="img" aria-label={`Miniatura temporal de ${group.doses.length} dose(s)`}>
      <path d={`M8,${height - 8} H${width - 8}`} fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="1.5" />
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary" />
      {points.map((point, index) => <circle key={index} cx={point.x} cy={point.y} r="3" fill="currentColor" className="text-primary" />)}
    </svg>
  );
}

export default function Journal() {
  const { data: patients = [] } = usePatients();
  const qc = useQueryClient();
  const [patientId, setPatientId] = useState(PRIMARY_PATIENT_ID);
  const [ingestionOpen, setIngestionOpen] = useState(false);
  const [vitalsOpen, setVitalsOpen] = useState(false);
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const historySince = CLINICAL_WINDOW_START;
  const bodySince = CLINICAL_WINDOW_START;
  const knowledge = useMemo(() => getRuntimeKnowledgeBase(), []);

  useEffect(() => {
    if (!patientId || !patients.length) return;
    if (patients.some((patient: any) => patient.id === patientId)) return;
    setPatientId("");
    localStorage.removeItem(PATIENT_STORAGE_KEY);
  }, [patients, patientId]);

  useEffect(() => {
    setIngestionOpen(false);
    setVitalsOpen(false);
    setCheckinOpen(false);
  }, [patientId]);

  const patient = (patients as any[]).find((row) => row.id === patientId);

  const { data: medications = [] } = useQuery({
    queryKey: ["journal-medications", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("patient_medications")
        .select("*, substances(name), substance_formulations(formulation_name)")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .order("usual_time", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: doses = [] } = useQuery({
    queryKey: ["journal-doses", patientId, historySince],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("medication_dose_logs")
        .select("*, substances(name)")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("created_at", historySince)
        .lt("created_at", CLINICAL_WINDOW_END)
        .order("actual_time", { ascending: false })
        .limit(1800);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: bodyLoadDoses = [] } = useQuery({
    queryKey: ["journal-body-load", patientId, bodySince],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("medication_dose_logs")
        .select("*, substances(*), substance_formulations(*)")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("created_at", bodySince)
        .lt("created_at", CLINICAL_WINDOW_END)
        .order("actual_time", { ascending: false })
        .limit(1800);
      if (error) throw error;
      return data ?? [];
    },
    refetchInterval: 60_000,
  });

  const { data: vitalSigns = [], isLoading: vitalsLoading } = useQuery({
    queryKey: ["journal-vitals", patientId],
    enabled: !!patientId,
    queryFn: async () => (await readPatientVitals(patientId)).filter((row: any) => {
      const t = new Date(row.created_at ?? row.measured_at).getTime();
      return t >= new Date(CLINICAL_WINDOW_START).getTime() && t < new Date(CLINICAL_WINDOW_END).getTime();
    }),
  });

  const { data: checkins = [], isLoading: checkinsLoading } = useQuery({
    queryKey: ["journal-checkins", patientId, historySince],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("session_checkins")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("created_at", historySince)
        .lt("created_at", CLINICAL_WINDOW_END)
        .order("checkin_at", { ascending: false })
        .limit(600);
      if (error) throw error;
      return data ?? [];
    },
  });

  const activeMeds = useMemo(
    () => (medications as any[]).filter((med) => !/suspens|descontinu|encerr/i.test(String(med.status ?? ""))),
    [medications],
  );
  const days = useMemo(() => groupDays(doses as any[]), [doses]);
  const bodyLoad = useMemo(
    () => buildBodyLoad(bodyLoadDoses as any[], { now: new Date(now), knowledge, bodyWeightKg: patient?.weight_kg == null ? undefined : Number(patient.weight_kg) } as any),
    [bodyLoadDoses, now, knowledge, patient?.weight_kg],
  );
  const activeGroups = bodyLoad.groups.slice(0, 4);

  function selectPatient(next: string) {
    setPatientId(next);
    localStorage.setItem(PATIENT_STORAGE_KEY, next);
  }

  async function refreshClinicalData() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["journal-vitals", patientId] }),
      qc.invalidateQueries({ queryKey: ["vital-signs", patientId] }),
      qc.invalidateQueries({ queryKey: ["journal-checkins", patientId] }),
      qc.invalidateQueries({ queryKey: ["psylog-events", patientId] }),
    ]);
  }

  async function refreshAfterIngestion() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["journal-doses", patientId] }),
      qc.invalidateQueries({ queryKey: ["journal-body-load", patientId] }),
      qc.invalidateQueries({ queryKey: ["insights-doses", patientId] }),
      qc.invalidateQueries({ queryKey: ["insights-body-load", patientId] }),
      qc.invalidateQueries({ queryKey: ["my-space-doses", patientId] }),
      qc.invalidateQueries({ queryKey: ["recent-doses", patientId] }),
      qc.invalidateQueries({ queryKey: ["curves-doses", patientId] }),
      qc.invalidateQueries({ queryKey: ["all-doses"] }),
      qc.invalidateQueries({ queryKey: ["psylog-events", patientId] }),
    ]);
  }

  return (
    <div className="clinical-command mx-auto w-full max-w-5xl space-y-5 pb-[calc(1rem+env(safe-area-inset-bottom))]">
      <header className="sticky top-0 z-10 -mx-1 flex items-end justify-between gap-3 border-b bg-background/95 px-1 py-3 backdrop-blur-xl sm:static sm:border-0 sm:bg-transparent sm:py-1">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">ClinicalOS</div>
          <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">Journal</h1>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {patientId ? (
            <Button asChild variant="outline" className="hidden rounded-full sm:inline-flex">
              <Link to={`/sessoes?patient=${patientId}&new=1`}><CalendarClock className="mr-1.5 h-4 w-4" />Criar sessão</Link>
            </Button>
          ) : null}
          <Button asChild variant="outline" size="icon" className="h-11 w-11 shrink-0 rounded-full">
            <Link to="/curvas" aria-label="Abrir curvas completas"><LineChart className="h-5 w-5" /></Link>
          </Button>
        </div>
      </header>

      <Card className="rounded-3xl p-3">
        <label htmlFor="journal-patient" className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Perfil</label>
        <select id="journal-patient" value={patientId} onChange={(event) => selectPatient(event.target.value)} className="mt-1 h-11 w-full rounded-xl border border-input bg-background px-3 text-base">
          <option value="">Selecione…</option>
          {patients.map((patientRow: any) => <option key={patientRow.id} value={patientRow.id}>{patientRow.social_name || patientRow.full_name}</option>)}
        </select>
      </Card>

      {patientId ? (
        <>
          <Card className="rounded-2xl border-primary/20 bg-primary/5 p-3 text-xs leading-relaxed text-muted-foreground">
            <strong className="text-foreground">Sessão é opcional.</strong> Tudo registrado aqui fica vinculado ao paciente e aparece no Journal/Timeline mesmo sem consulta aberta.
          </Card>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Button type="button" className="h-12" onClick={() => setIngestionOpen(true)}><Plus className="mr-2 h-4 w-4" />Registrar dose</Button>
            <Button type="button" variant="outline" className="h-12" onClick={() => setVitalsOpen(true)}><HeartPulse className="mr-2 h-4 w-4" />Registrar PA/FC</Button>
            <Button type="button" variant="outline" className="h-12" onClick={() => setCheckinOpen(true)}><CheckCircle2 className="mr-2 h-4 w-4" />Avaliação clínica</Button>
          </div>

          {(vitalsLoading || checkinsLoading) ? <Card className="p-3 text-xs text-muted-foreground">Atualizando sinais vitais e avaliações…</Card> : null}
          <PsyLogTimelinePanel doses={doses as any[]} vitalSigns={vitalSigns as any[]} checkins={checkins as any[]} />

                    <Link to="/medicamentos" className="block rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Card className="rounded-3xl p-4 hover:bg-muted/40">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><h2 className="font-semibold">Meu tratamento</h2><Badge variant="secondary">{activeMeds.length}</Badge></div>
                  <div className="mt-3 space-y-1.5">
                    {activeMeds.slice(0, 3).map((med: any) => (
                      <div key={med.id} className="flex min-w-0 items-center justify-between gap-3 text-sm">
                        <span className="truncate text-muted-foreground">{med.substances?.name ?? med.free_text_name ?? med.brand_name ?? "Medicação"}</span>
                        <span className="shrink-0 tabular-nums">{[med.current_dose != null ? `${med.current_dose} ${med.dose_unit ?? ""}`.trim() : null, med.usual_time ? String(med.usual_time).slice(0, 5) : null].filter(Boolean).join(" · ")}</span>
                      </div>
                    ))}
                    {!activeMeds.length ? <div className="text-sm text-muted-foreground">Nenhum item ativo cadastrado.</div> : null}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              </div>
            </Card>
          </Link>

          <Link to="/insights" className="block rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Card className="rounded-3xl p-4 hover:bg-muted/40">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><Activity className="h-4 w-4 text-primary" /><h2 className="font-semibold">Ativo agora</h2></div>
                  <div className="mt-3 space-y-2">
                    {activeGroups.length ? activeGroups.map((group) => (
                      <div key={group.key} className="flex items-center justify-between gap-3 text-sm">
                        <span className="truncate text-muted-foreground">{group.canonicalName}</span>
                        <span className="shrink-0 font-medium tabular-nums">≈ {group.remainingMid >= 10 ? group.remainingMid.toFixed(0) : group.remainingMid.toFixed(2)} {group.unit}</span>
                      </div>
                    )) : <div className="text-sm text-muted-foreground">Nenhuma carga corporal estimável com os parâmetros atuais.</div>}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              </div>
            </Card>
          </Link>

          <section className="space-y-4">
            {days.slice(0, 14).map((group) => (
              <div key={group.key} className="space-y-2">
                <div className="flex items-baseline gap-2 px-1">
                  <h2 className="text-lg font-semibold capitalize">{sameDayLabel(group.key)}</h2>
                  <span className="text-sm capitalize text-muted-foreground">{weekday(group.key)}</span>
                </div>
                <Link to={`/journal/${group.key}?patient=${patientId}`} className="block rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Card className="rounded-3xl p-4 hover:bg-muted/40">
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-semibold"><Clock3 className="h-4 w-4 text-muted-foreground" />{timeLabel(group.first)} – {timeLabel(group.last)}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{group.doses.length} dose(s)</div>
                        <div className="mt-3 flex min-w-0 items-center gap-2">
                          <div className="flex gap-1">{group.names.slice(0, 4).map((name, index) => <span key={`${name}-${index}`} className="h-2.5 w-2.5 rounded-full bg-primary" style={{ opacity: Math.max(0.35, 1 - index * 0.16) }} />)}</div>
                          <span className="truncate text-sm text-muted-foreground">{group.names.slice(0, 3).join(", ")}{group.names.length > 3 ? ` +${group.names.length - 3}` : ""}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <MiniDoseTrace group={group} />
                        <ChevronRight className="h-5 w-5" />
                      </div>
                    </div>
                  </Card>
                </Link>
              </div>
            ))}
            {!days.length && !(vitalSigns as any[]).length && !(checkins as any[]).length ? <Card className="rounded-3xl p-6 text-center text-sm text-muted-foreground">Ainda não há registros no período. Use os botões acima para dose, PA/FC ou avaliação clínica.</Card> : null}
          </section>

          <div className="sticky bottom-[calc(5.2rem+env(safe-area-inset-bottom))] z-20 flex flex-wrap justify-end gap-2 pointer-events-none">
            <Button type="button" size="lg" variant="outline" className="pointer-events-auto h-14 rounded-full bg-background/95 px-5 shadow-xl backdrop-blur" onClick={() => setVitalsOpen(true)}>
              <HeartPulse className="mr-2 h-5 w-5" />PA / FC
            </Button>
            <Button type="button" size="lg" className="pointer-events-auto h-14 rounded-full px-5 shadow-xl" onClick={() => setIngestionOpen(true)}>
              <Plus className="mr-2 h-5 w-5" />Dose / ingestão
            </Button>
          </div>

          <QuickIngestionWizard open={ingestionOpen} onOpenChange={setIngestionOpen} patientId={patientId} onSaved={refreshAfterIngestion} />
          <Dialog open={vitalsOpen} onOpenChange={setVitalsOpen}>
            <DialogContent className="max-h-[94dvh] overflow-y-auto sm:max-w-5xl">
              <DialogHeader><DialogTitle>Registrar PA / FC · {patient?.social_name || patient?.full_name}</DialogTitle></DialogHeader>
              <VitalSignsPanel key={`journal-vitals-${patientId}`} patientId={patientId} onSaved={() => { setVitalsOpen(false); void refreshClinicalData(); }} />
            </DialogContent>
          </Dialog>
          <Dialog open={checkinOpen} onOpenChange={setCheckinOpen}>
            <DialogContent className="max-h-[94dvh] overflow-y-auto sm:max-w-4xl">
              <DialogHeader><DialogTitle>Avaliação clínica · {patient?.social_name || patient?.full_name}</DialogTitle></DialogHeader>
              <SessionCheckin key={`journal-checkin-${patientId}`} patientId={patientId} onSaved={() => { setCheckinOpen(false); void refreshClinicalData(); }} />
            </DialogContent>
          </Dialog>
        </>
      ) : <Card className="rounded-3xl p-6 text-center text-sm text-muted-foreground">Selecione um perfil para abrir o Journal.</Card>}
    </div>
  );
}
